import apiClient from "./apiClient";
import { formatApiError } from "../lib/laravelError";

/** Verificación de lead vía enlace firmado (query: expires, signature, …). */
export async function verifyLead(leadId, hash, queryParams = {}) {
  try {
    const { data } = await apiClient.get(
      `/lead/verify/${encodeURIComponent(leadId)}/${encodeURIComponent(hash)}`,
      { params: queryParams }
    );
    return data;
  } catch (err) {
    throw new Error(formatApiError(err, "No se pudo verificar el enlace"));
  }
}

/**
 * Comprueba el enlace de bienvenida/onboarding (firma en query).
 * Los segmentos y el orden los define el backend; deben coincidir con la URL del SPA
 * después de `/rules/` (o legacy `/lead/welcome-check/`).
 *
 * **Ruta React (canónica):** `/rules/:splat` → p. ej.
 * `https://app.zyrisx.com/rules/2/TOKEN?expires=…&signature=…`
 *
 * **Petición API:** `GET {REACT_APP_API_BASE_URL}/lead/welcome-check/2/TOKEN?expires=…&signature=…`
 *
 * **Ejemplo con TanStack Query:**
 * ```js
 * const suffix = "2/6d9cb706deffdae3c0db32c2b8d43195d6cfacb6";
 * const queryParams = { expires: "1778452023", signature: "8d016af4…" };
 * useQuery({
 *   queryKey: ["lead", "welcome-check", suffix, queryParams],
 *   queryFn: () => welcomeCheckLead(suffix, queryParams),
 *   enabled: Boolean(suffix),
 *   retry: false,
 * });
 * ```
 */
export async function welcomeCheckLead(pathAfterWelcomeCheck, queryParams = {}) {
  const normalized = String(pathAfterWelcomeCheck || "")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  if (!normalized) {
    throw new Error("Enlace incompleto");
  }

  try {
    const { data } = await apiClient.get(`/lead/welcome-check/${normalized}`, {
      params: queryParams,
    });
    return data;
  } catch (err) {
    throw new Error(formatApiError(err, "No se pudo validar el enlace"));
  }
}

/** Primer segmento del path tras `/rules/` (p. ej. `2/token` → `"2"`). */
export function parseLeadRequestIdFromSuffix(pathSuffix) {
  return String(pathSuffix || "")
    .split("/")
    .filter(Boolean)[0] || "";
}

/**
 * Interpreta el JSON de comprobación de nickname (el back puede usar distintas claves).
 * @returns {boolean|null} true = libre, false = ocupado, null = no se pudo inferir
 */
export function inferNicknameAvailable(data) {
  if (data == null || typeof data !== "object") return null;
  if (typeof data.available === "boolean") return data.available;
  if (typeof data.free === "boolean") return data.free;
  if (typeof data.taken === "boolean") return !data.taken;
  if (typeof data.exists === "boolean") return !data.exists;
  if (typeof data.valid === "boolean") return data.valid;
  return null;
}

/**
 * POST `/lead/register/check-nickname` (base API ya incluye `/v1`).
 * Body JSON: `{ "nickname": "..." }`
 */
export async function checkRegisterNickname(nickname, options = {}) {
  try {
    const { data } = await apiClient.post(
      "/lead/register/check-nickname",
      { nickname: String(nickname || "").trim() },
      options
    );
    return data;
  } catch (err) {
    throw new Error(formatApiError(err, "No se pudo comprobar el nickname"));
  }
}

/**
 * POST `/lead/register` — alta tras onboarding con enlace firmado.
 *
 * **Body JSON enviado al API:**
 * ```json
 * {
 *   "id": "<id de la petición, p. ej. segmento inicial de /rules/2/…>",
 *   "nickname": "...",
 *   "password": "...",
 *   "password_confirmation": "<misma que password, por validación Laravel>"
 * }
 * ```
 */
export async function registerLeadWelcome(
  { id, nickname, password },
  options = {}
) {
  const nick = String(nickname || "").trim();
  try {
    const { data } = await apiClient.post(
      "/lead/register",
      {
        id,
        nickname: nick,
        password,
        password_confirmation: password,
      },
      options
    );
    return data;
  } catch (err) {
    throw new Error(formatApiError(err, "No se pudo completar el registro"));
  }
}
